import {
  addSessionStorageItem,
  deletesessionStorageItem,
  getsessionStorageItem,
} from "../helpers/sessionStorageHelper";
import { SessionStorageKeys } from "./constants";
import { aesCtrDecryptNoSpecialChars } from "./common";

// Query params used by private list pages to auto-open linked files.
export const AGREEMENT_DEEP_LINK_QUERY_PARAM = "agreement";
export const DOCUMENT_DEEP_LINK_QUERY_PARAM = "doc";
export const APPLICATION_DEEP_LINK_QUERY_PARAM = "application";
export const APPLICATION_INVITE_QUERY_PARAM = "invite";

export const getCurrentDeepLinkUrl = () => {
  const { pathname, search, hash } = window.location;
  return `${pathname}${search}${hash}`;
};

// Keep the original deeplink while the user is redirected through login/setup.
export const setPendingDeepLinkUrl = (url = getCurrentDeepLinkUrl()) => {
  if (!url) return;
  addSessionStorageItem(SessionStorageKeys.PendingDeepLinkUrl, url);
};

export const getPendingDeepLinkUrl = () =>
  getsessionStorageItem(SessionStorageKeys.PendingDeepLinkUrl, "");

export const clearPendingDeepLinkUrl = () => {
  deletesessionStorageItem(SessionStorageKeys.PendingDeepLinkUrl);
};

export const consumePendingDeepLinkUrl = () => {
  const url = getPendingDeepLinkUrl();
  clearPendingDeepLinkUrl();
  return url;
};

export const clearUrlQueryParam = (paramName) => {
  const url = new URL(window.location);
  url.searchParams.delete(paramName);
  window.history.replaceState({}, "", url);
};

export const getPathWithQueryParam = (path, paramName, value) => {
  const url = new URL(path, window.location.origin);
  url.searchParams.set(paramName, value);
  return `${url.pathname}${url.search}${url.hash}`;
};

// Agreement file links are AES-CTR encrypted. Supports old 4-part links and new 6-part links.
export const parseAgreementDeepLinkPayload = async (token) => {
  const decryptedId = await aesCtrDecryptNoSpecialChars(token);
  const parts = (decryptedId || "").split(":");

  if (parts.length < 4) {
    throw new Error("Invalid agreement link.");
  }

  const parsedParts = parts.map((part) => parseInt(part, 10));
  const usesSharedRecipientPayload =
    parts.length >= 6 &&
    !Number.isNaN(parsedParts[3]) &&
    !Number.isNaN(parsedParts[4]);

  const agreementId = parsedParts[0];
  const accountId = parsedParts[1];
  const profileId = parsedParts[2];
  const sharedAccountId = usesSharedRecipientPayload ? parsedParts[3] : 0;
  const sharedProfileId = usesSharedRecipientPayload ? parsedParts[4] : 0;
  const agreementCreatedTime = usesSharedRecipientPayload
    ? parts.slice(5).join(":")
    : parts.slice(3).join(":");

  if (
    [agreementId, accountId, profileId, sharedAccountId, sharedProfileId].some(
      (value) => Number.isNaN(value),
    ) ||
    agreementCreatedTime === ""
  ) {
    throw new Error("Invalid agreement link.");
  }

  return {
    agreementId,
    accountId,
    profileId,
    sharedAccountId,
    sharedProfileId,
    agreementCreatedTime,
  };
};

// Document file links come in two shapes:
//   base   (4 parts): documentId:accountId:profileId:createdTime
//   shared (6 parts): documentId:senderAccountId:senderProfileId:recipientAccountId:recipientProfileId:createdTime
//                     (minted by EmailHelper.SendShareDocumentInvitationEmails)
export const parseDocumentDeepLinkPayload = async (token) => {
  const decryptedId = await aesCtrDecryptNoSpecialChars(token);
  const parts = (decryptedId || "").split(":");

  if (parts.length < 4) {
    throw new Error("Invalid document link.");
  }

  const parsedParts = parts.map((part) => parseInt(part, 10));
  const usesSharedRecipientPayload =
    parts.length >= 6 &&
    !Number.isNaN(parsedParts[3]) &&
    !Number.isNaN(parsedParts[4]);

  const documentId = parsedParts[0];
  const accountId = parsedParts[1];
  const profileId = parsedParts[2];
  const sharedAccountId = usesSharedRecipientPayload ? parsedParts[3] : 0;
  const sharedProfileId = usesSharedRecipientPayload ? parsedParts[4] : 0;
  const documentCreatedTime = usesSharedRecipientPayload
    ? parts.slice(5).join(":")
    : parts.slice(3).join(":");

  if (
    [documentId, accountId, profileId, sharedAccountId, sharedProfileId].some(
      (value) => Number.isNaN(value),
    ) ||
    documentCreatedTime === ""
  ) {
    throw new Error("Invalid document link.");
  }

  return {
    documentId,
    accountId,
    profileId,
    sharedAccountId,
    sharedProfileId,
    documentCreatedTime,
  };
};

// Application deep links (from v2 send/add invitation emails) are AES-CTR
// encrypted. Two payload shapes supported:
//   new (8 parts): applicationId : senderAccountId : senderProfileId
//     : recipientAccountId : recipientProfileId : formId : recipientKind : timestamp
//   legacy (7 parts, pre-2026-08-18): drops recipientKind — treated as 0
//     (existing user) so old invitation URLs still work.
// Matches the payload built by EmailHelper.SendApplicationInvitationEmailsV2.
export const parseApplicationDeepLinkPayload = async (token) => {
  const decryptedId = await aesCtrDecryptNoSpecialChars(token);
  const parts = (decryptedId || "").split(":");

  if (parts.length < 7) {
    throw new Error("Invalid application link.");
  }

  const parsedParts = parts.map((part) => parseInt(part, 10));
  const applicationId = parsedParts[0];
  const senderAccountId = parsedParts[1];
  const senderProfileId = parsedParts[2];
  const recipientAccountId = parsedParts[3];
  const recipientProfileId = parsedParts[4];
  const formId = parsedParts[5];

  // 8-part = new shape with recipientKind; 7-part = legacy (default kind=0).
  const hasKind = parts.length >= 8;
  const recipientKind = hasKind ? parsedParts[6] : 0;
  // Timestamp is a plain yyyyMMddHHmmss string — kept as-is (may not fit int).
  const timestamp = hasKind ? parts.slice(7).join(":") : parts.slice(6).join(":");

  if (
    [
      applicationId,
      senderAccountId,
      senderProfileId,
      recipientAccountId,
      recipientProfileId,
      formId,
    ].some((value) => Number.isNaN(value)) ||
    timestamp === ""
  ) {
    throw new Error("Invalid application link.");
  }

  return {
    applicationId,
    senderAccountId,
    senderProfileId,
    recipientAccountId,
    recipientProfileId,
    formId,
    recipientKind,
    timestamp,
  };
};

// Folder links come in three shapes:
//   root   (4 parts): folderId:accountId:profileId:createdTime
//   nested (5 parts): folderId:accountId:profileId:parentFolderId:createdTime  (built by FoldersViewer when drilling in)
//   shared (6 parts): folderId:senderAccountId:senderProfileId:recipientAccountId:recipientProfileId:createdTime
//                     (minted by EmailHelper.SendShareDocumentInvitationEmails)
export const parseDocumentFolderDeepLinkPayload = async (token) => {
  const decryptedId = await aesCtrDecryptNoSpecialChars(token);
  const parts = (decryptedId || "").split(":");

  if (parts.length < 4) {
    throw new Error("Invalid document link.");
  }

  const parsedParts = parts.map((part) => parseInt(part, 10));
  const usesSharedRecipientPayload =
    parts.length >= 6 &&
    !Number.isNaN(parsedParts[3]) &&
    !Number.isNaN(parsedParts[4]);
  const usesNestedFolderPayload =
    !usesSharedRecipientPayload &&
    parts.length >= 5 &&
    !Number.isNaN(parsedParts[3]);

  const folderId = parsedParts[0];
  const accountId = parsedParts[1];
  const profileId = parsedParts[2];
  const parentFolderId = usesNestedFolderPayload ? parsedParts[3] : 0;
  const sharedAccountId = usesSharedRecipientPayload ? parsedParts[3] : 0;
  const sharedProfileId = usesSharedRecipientPayload ? parsedParts[4] : 0;
  const folderCreatedDatetime = usesSharedRecipientPayload
    ? parts.slice(5).join(":")
    : usesNestedFolderPayload
      ? parts.slice(4).join(":")
      : parts.slice(3).join(":");

  if (
    [
      folderId,
      accountId,
      profileId,
      parentFolderId,
      sharedAccountId,
      sharedProfileId,
    ].some((value) => Number.isNaN(value)) ||
    folderCreatedDatetime === ""
  ) {
    throw new Error("Invalid document link.");
  }

  return {
    folderId,
    accountId,
    profileId,
    parentFolderId,
    sharedAccountId,
    sharedProfileId,
    folderCreatedDatetime,
  };
};
